// src/tareas/CrearTarea.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

export const CrearTarea = () => {
  const { auth } = useAuth();
  const toast = useToast();
  const creadorId = auth?.id;

  // ===== Form =====
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState("normal"); // baja|normal|alta|critica
  const [fechaLimite, setFechaLimite] = useState("");

  // ===== Data =====
  const [departamentos, setDepartamentos] = useState([]);
  const [deptoSel, setDeptoSel] = useState(null); // {id,nombre,...} | null

  const [usuariosDepto, setUsuariosDepto] = useState([]);
  const [destUsuarios, setDestUsuarios] = useState([]); // array de usuarios (objetos)

  // ===== Loading =====
  const [cargando, setCargando] = useState(false);
  const [cargandoDeptos, setCargandoDeptos] = useState(false);
  const [cargandoUsuariosDepto, setCargandoUsuariosDepto] = useState(false);

  const authHeadersJson = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // =========================
  // 1) Cargar departamentos activos
  // =========================
  useEffect(() => {
    (async () => {
      setCargandoDeptos(true);
      try {
        const res = await fetch(`${Global.url}departamentos`, { headers: authHeaders() });
        if (!res.ok) {
          setDepartamentos([]);
          return;
        }
        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data.filter((d) => d.activo !== false) : [];
        setDepartamentos(lista);
      } catch (e) {
        console.error("Error cargando departamentos para tareas:", e);
        setDepartamentos([]);
      } finally {
        setCargandoDeptos(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // 2) Cargar usuarios del depto
  // =========================
  useEffect(() => {
    if (!deptoSel?.id) {
      setUsuariosDepto([]);
      setDestUsuarios([]);
      return;
    }

    (async () => {
      setCargandoUsuariosDepto(true);
      try {
        const res = await fetch(`${Global.url}departamentos/${deptoSel.id}/usuarios`, {
          headers: authHeaders(),
        });
        if (!res.ok) {
          setUsuariosDepto([]);
          setDestUsuarios([]);
          return;
        }
        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data.filter((u) => u.miembro_activo !== false) : [];
        setUsuariosDepto(lista);
        setDestUsuarios([]);
      } catch (e) {
        console.error("Error cargando usuarios del departamento para tareas:", e);
        setUsuariosDepto([]);
        setDestUsuarios([]);
      } finally {
        setCargandoUsuariosDepto(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptoSel?.id]);

  // =========================
  // Opciones
  // =========================
  const deptoOptions = useMemo(() => {
    return (departamentos || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((d) => ({
        id: Number(d.id),
        nombre: d.nombre || `Depto #${d.id}`,
        raw: d,
      }));
  }, [departamentos]);

  const usuariosOptions = useMemo(() => {
    return (usuariosDepto || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre || ""} ${a.apellidos || ""}`.localeCompare(`${b.nombre || ""} ${b.apellidos || ""}`)
      )
      .map((u) => ({
        id: Number(u.id),
        nombre: u.nombre || "",
        apellidos: u.apellidos || "",
        email: u.email || "",
        dni: u.dni || "",
        raw: u,
      }));
  }, [usuariosDepto]);

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cargando) return;

    if (!creadorId) {
      toast.error?.("No se pudo determinar el usuario creador (auth.id).");
      return;
    }

    if (!titulo.trim() || !descripcion.trim()) {
      toast.warning?.("Título y descripción son obligatorios.");
      return;
    }

    if (!deptoSel?.id) {
      toast.warning?.("Debes seleccionar un departamento responsable.");
      return;
    }

    const destinatariosUsuarios = (destUsuarios || []).map((u) => Number(u.id));
    const destinatariosDepartamentos = [Number(deptoSel.id)];

    const payload = {
      titulo: titulo.trim(),
      descripcion: descripcion.trim(),
      prioridad,
      fecha_vencimiento: fechaLimite || null,
      departamento_id: Number(deptoSel.id),
      destinatarios_usuarios: destinatariosUsuarios,
      destinatarios_departamentos: destinatariosDepartamentos,
    };

    try {
      setCargando(true);
      const res = await fetch(`${Global.url}tareas`, {
        method: "POST",
        headers: authHeadersJson(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Error al crear tarea:", data);
        toast.error?.(data?.message || "No se pudo crear la tarea.");
        return;
      }

      toast.success?.("Tarea creada correctamente.");

      // Reset (mantenemos depto para crear varias seguidas)
      setTitulo("");
      setDescripcion("");
      setPrioridad("normal");
      setFechaLimite("");
      setDestUsuarios([]);
    } catch (error) {
      console.error("Error al crear tarea:", error);
      toast.error?.("Error de red al crear la tarea.");
    } finally {
      setCargando(false);
    }
  };

  // =========================
  // Render
  // =========================
  const disabledDestinatarios = !deptoSel?.id || usuariosOptions.length === 0;

  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Crear nueva tarea
        </Typography>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Paper sx={{ p: 2, width: "100%", maxWidth: 900 }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                alignItems: "end",
              }}
            >
              {/* Departamento */}
              <Autocomplete
                size="small"
                options={deptoOptions}
                value={deptoSel}
                onChange={(_, v) => setDeptoSel(v)}
                loading={cargandoDeptos}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                getOptionLabel={(o) => o?.nombre || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departamento responsable"
                    placeholder="Selecciona un departamento…"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cargandoDeptos ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                clearOnEscape
              />

              {/* Prioridad */}
              <TextField
                select
                size="small"
                label="Prioridad"
                value={prioridad}
                onChange={(e) => setPrioridad(e.target.value)}
              >
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="normal">Media</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
              </TextField>

              {/* Título (full width en md) */}
              <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
                <TextField
                  size="small"
                  label="Título"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder="Ej: Revisar informe mensual"
                  required
                  fullWidth
                />
              </Box>

              {/* Descripción (full width en md) */}
              <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
                <TextField
                  size="small"
                  label="Descripción"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Describe qué se necesita hacer, contexto, detalles…"
                  required
                  fullWidth
                  multiline
                  minRows={4}
                />
              </Box>

              {/* Fecha límite */}
              <TextField
                size="small"
                label="Fecha límite (opcional)"
                type="date"
                value={fechaLimite}
                onChange={(e) => setFechaLimite(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              {/* Destinatarios (usuarios depto) */}
              <Autocomplete
                multiple
                size="small"
                options={usuariosOptions}
                value={destUsuarios}
                onChange={(_, v) => setDestUsuarios(v || [])}
                loading={cargandoUsuariosDepto}
                disabled={disabledDestinatarios}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                getOptionLabel={(o) =>
                  `${o?.nombre || ""} ${o?.apellidos || ""} (${o?.email || o?.dni || "s/d"})`.trim()
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destinatarios (usuarios del departamento)"
                    placeholder={
                      !deptoSel?.id
                        ? "Selecciona primero un departamento…"
                        : usuariosOptions.length === 0
                        ? "Este departamento no tiene usuarios activos."
                        : "Selecciona uno o varios usuarios…"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cargandoUsuariosDepto ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                clearOnEscape
              />

              {/* Ayuda (full width) */}
              <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  La tarea se enviará <b>siempre</b> a la cola del departamento seleccionado. Si además
                  eliges usuarios, aparecerá asociada a ellos en sus tableros personales.
                </Typography>
              </Box>

              {/* Botón (alineado a la derecha, misma fila en md) */}
              <Box sx={{ gridColumn: { xs: "auto", md: "span 2" }, display: "flex", justifyContent: "flex-end" }}>
                <Button type="submit" disabled={cargando} sx={{ height: 40, minWidth: 180 }}>
                  {cargando ? "Creando..." : "Crear tarea"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default CrearTarea;
